import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import BrandMonogram, { WordmarkLockup } from "@/components/BrandMonogram";
import ThemeToggleButton from "@/components/ThemeToggleButton";
import { COUNTRIES } from "@/data/countries";
import { ChevronDown } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || null;
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "",
    job_title: "", company_name: "",
    address_line1: "", city: "", address_postcode: "", country: "",
  });
  const [loading, setLoading] = useState(false);

  function onChange(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.first_name.trim()) {
      toast.error("First name is required.");
      return;
    }
    if (!form.job_title.trim()) {
      toast.error("Job title is required.");
      return;
    }
    if (!form.address_line1.trim()) {
      toast.error("Address line 1 is required.");
      return;
    }
    if (!form.city.trim()) {
      toast.error("City is required.");
      return;
    }
    if (!form.address_postcode.trim()) {
      toast.error("Postcode is required.");
      return;
    }
    if (form.password.length < 12) {
      toast.error("Password must be at least 12 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(
        form.email.trim(), form.password,
        form.first_name.trim(), form.last_name.trim(),
        refCode,
        {
          job_title: form.job_title.trim(),
          company_name: form.company_name.trim(),
          address_line1: form.address_line1.trim(),
          city: form.city.trim(),
          address_postcode: form.address_postcode.trim(),
          country: form.country,
        },
      );
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bf-bg flex flex-col" data-testid="register-page">
      <nav className="px-8 py-5 flex items-center justify-between border-b border-[#1D1D22]">
        <Link to="/" data-testid="register-nav-logo"><WordmarkLockup /></Link>
        <div className="flex items-center gap-4">
          <span className="text-[12px] font-mono text-[#737373] uppercase tracking-[0.18em]">
            Already have an account?
          </span>
          <Link
            to="/login"
            data-testid="register-nav-login"
            className="text-[12px] font-mono uppercase tracking-[0.18em] border border-[#3F3F3F] hover:border-white text-white px-3 py-1.5 rounded-md transition-colors"
          >
            Sign in
          </Link>
          <ThemeToggleButton />
        </div>
      </nav>

      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="mb-10 text-center">
            <BrandMonogram size={44} className="mx-auto mb-6" />
            <h1
              className="text-[30px] tracking-[-0.02em] leading-tight"
              style={{ fontFamily: "Sora, sans-serif", fontWeight: 600 }}
              data-testid="register-heading"
            >
              Create your account
            </h1>
            {refCode && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#6D5DF6]/40 bg-[#6D5DF6]/10">
                <span className="text-[11px] font-mono text-[#A3A3A3]">
                  Referral <span style={{ color: "var(--bf-violet)" }}>{refCode}</span> — your inviter earns a discount when you join
                </span>
              </div>
            )}
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
            {/* Name row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="register-first-name" className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#737373] block mb-1.5">
                  First name *
                </label>
                <input
                  id="register-first-name"
                  name="first_name"
                  value={form.first_name}
                  onChange={onChange}
                  required
                  autoComplete="given-name"
                  className="w-full bg-transparent border border-[#262626] rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-[#6D5DF6] transition-colors"
                  placeholder="Jane"
                  data-testid="register-first-name-input"
                />
              </div>
              <div>
                <label htmlFor="register-last-name" className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#737373] block mb-1.5">
                  Surname
                </label>
                <input
                  id="register-last-name"
                  name="last_name"
                  value={form.last_name}
                  onChange={onChange}
                  autoComplete="family-name"
                  className="w-full bg-transparent border border-[#262626] rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-[#6D5DF6] transition-colors"
                  placeholder="Smith"
                  data-testid="register-last-name-input"
                />
              </div>
            </div>

            {/* Job title + Company */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="register-job-title" className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#737373] block mb-1.5">
                  Job title *
                </label>
                <input
                  id="register-job-title"
                  name="job_title"
                  value={form.job_title}
                  onChange={onChange}
                  autoComplete="organization-title"
                  className="w-full bg-transparent border border-[#262626] rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-[#6D5DF6] transition-colors"
                  placeholder="Brand Designer"
                  data-testid="register-job-title-input"
                />
              </div>
              <div>
                <label htmlFor="register-company" className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#737373] block mb-1.5">
                  Company name
                </label>
                <input
                  id="register-company"
                  name="company_name"
                  value={form.company_name}
                  onChange={onChange}
                  autoComplete="organization"
                  className="w-full bg-transparent border border-[#262626] rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-[#6D5DF6] transition-colors"
                  placeholder="Acme Studio"
                  data-testid="register-company-input"
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <label htmlFor="register-address" className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#737373] block mb-1.5">
                Address line 1 *
              </label>
              <input
                id="register-address"
                name="address_line1"
                value={form.address_line1}
                onChange={onChange}
                autoComplete="address-line1"
                className="w-full bg-transparent border border-[#262626] rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-[#6D5DF6] transition-colors"
                placeholder="12 High Street"
                data-testid="register-address-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="register-city" className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#737373] block mb-1.5">
                  City *
                </label>
                <input
                  id="register-city"
                  name="city"
                  value={form.city}
                  onChange={onChange}
                  autoComplete="address-level2"
                  className="w-full bg-transparent border border-[#262626] rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-[#6D5DF6] transition-colors"
                  placeholder="London"
                  data-testid="register-city-input"
                />
              </div>
              <div>
                <label htmlFor="register-postcode" className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#737373] block mb-1.5">
                  Postcode *
                </label>
                <input
                  id="register-postcode"
                  name="address_postcode"
                  value={form.address_postcode}
                  onChange={onChange}
                  autoComplete="postal-code"
                  className="w-full bg-transparent border border-[#262626] rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-[#6D5DF6] transition-colors"
                  placeholder="EC1A 1BB"
                  data-testid="register-postcode-input"
                />
              </div>
            </div>

            {/* Country */}
            <div>
              <label htmlFor="register-country" className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#737373] block mb-1.5">
                Country
              </label>
              <div className="relative">
                <select
                  id="register-country"
                  name="country"
                  value={form.country}
                  onChange={onChange}
                  autoComplete="country-name"
                  className="w-full appearance-none bg-[#141414] border border-[#262626] rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-[#6D5DF6] transition-colors pr-9"
                  data-testid="register-country-select"
                >
                  <option value="">Select country</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
              </div>
            </div>

            <div>
              <label htmlFor="register-email" className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#737373] block mb-1.5">
                Email address *
              </label>
              <input
                id="register-email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                required
                autoComplete="email"
                className="w-full bg-transparent border border-[#262626] rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-[#6D5DF6] transition-colors"
                placeholder="jane@yourbrand.com"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <label htmlFor="register-password" className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#737373] block mb-1.5">
                Password *
              </label>
              <input
                id="register-password"
                name="password"
                type="password"
                value={form.password}
                onChange={onChange}
                required
                autoComplete="new-password"
                className="w-full bg-transparent border border-[#262626] rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-[#6D5DF6] transition-colors"
                placeholder="12+ characters, uppercase, lowercase, numbers & symbols recommended"
                data-testid="register-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 text-sm font-semibold rounded-md transition-all disabled:opacity-50"
              style={{ background: "var(--bf-violet)", color: "#fff" }}
              data-testid="register-submit-button"
            >
              {loading ? "Creating account…" : "Create account"}
            </button>

            <p className="text-center text-[11px] text-[#525252] font-mono leading-relaxed">
              By creating an account you agree to our{" "}
              <Link to="/terms" className="text-[#737373] hover:text-white transition-colors">Terms</Link>
              {" "}and{" "}
              <Link to="/privacy" className="text-[#737373] hover:text-white transition-colors">Privacy Policy</Link>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
